// ***************************************************************************************
// The scoring rules, recovered from the C. See RULES.md for the derivation and line
// references; this file is the single place the numbers live.
//
// Nothing here may depend on rendering, timing, React or the network. The Worker imports
// this module to re-score a submitted game, so anything that cannot run in workerd does
// not belong in it.
// ***************************************************************************************

/** WORDSOUP_WORDLENGTH_MIN, game/wordsoup.cpp:27. */
export const WORD_LENGTH_MIN = 3;

/**
 * MOVELOG_MAX_TILES_PER_MOVE. The in-game selection limit is 31
 * (WORDSOUP_WORDLENGTH_MAX) but a move longer than this cannot be recorded or submitted,
 * so this is the limit that actually binds. On a 5x5 board neither can be reached.
 */
export const MAX_TILES_PER_MOVE = 16;

export const TIER_ORDINARY = 0;
export const TIER_NICE = 1;
export const TIER_COOL = 2;

export type Tier = typeof TIER_ORDINARY | typeof TIER_NICE | typeof TIER_COOL;

/**
 * WordSoup_GetLetterValue, game/wordsoup.cpp:1421.
 *
 * NOT the Scrabble table. It differs on i (3 not 1), l (5 not 1) and u (2 not 1) among
 * others, and substituting Scrabble values would silently re-balance every score in the
 * game.
 */
export const LETTER_VALUE: readonly number[] = [
  1, 3, 3, 2, 1, 4, 2, 4, 3, 8, 5, 5, 3,
  2, 1, 3, 10, 1, 1, 1, 2, 4, 4, 8, 4, 10,
];

/**
 * BlockManager_SetupNormalBlockWeights, game/blockmanager.cpp. Roughly English letter
 * frequency, hand-adjusted. Only the DEALT modes use this; a constructed board picks
 * whole words instead.
 */
export const LETTER_WEIGHT: readonly number[] = [
  76, 28, 30, 38, 112, 16, 28, 27, 51, 9, 23, 40, 32,
  62, 88, 37, 11, 60, 66, 64, 35, 15, 17, 12, 20, 16,
];

/** WordSoup_GetTierScorePercent, game/wordsoup.cpp:449. */
export function tierScorePercent(tier: number): number {
  if (tier === TIER_NICE) return 150;
  if (tier === TIER_COOL) return 250;

  return 100;
}

/**
 * The colour a word's score flies away in. From WordSoup_GetTierColour - the C comment
 * there is right that this is the part players actually notice.
 */
export function tierColour(tier: number): number {
  if (tier === TIER_NICE) return 0xffd640;
  if (tier === TIER_COOL) return 0x59ffff;

  return 0xffffff;
}

export function tierName(tier: number): string | null {
  if (tier === TIER_NICE) return 'Nice word';
  if (tier === TIER_COOL) return 'Cool word';

  return null;
}

export function letterValue(letter: string): number {
  const at = letter.charCodeAt(0) - 97;

  return LETTER_VALUE[at] ?? 0;
}

/**
 * WordSoup_GetWordScore, game/wordsoup.cpp:406.
 *
 *     score  = sum of letter values
 *     score *= length
 *     score  = (score * tierPercent) / 100     <- integer division, truncating
 *
 * The caller has already established that the word is real; this does the arithmetic and
 * nothing else, so the UI can price a selection before it is submitted.
 */
export function scoreWord(word: string, tier: number): number {
  if (word.length < WORD_LENGTH_MIN) return 0;

  let raw = 0;

  for (const c of word) raw += letterValue(c);

  raw *= word.length;

  // Math.floor, not a plain `/`: the C is integer arithmetic and 150% of an odd raw
  // score truncates rather than rounds.
  return Math.floor((raw * tierScorePercent(tier)) / 100);
}

// ***************************************************************************************
// Balance knobs that are NOT recovered rules.
//
// A clear-the-grid bonus exists in game/gameover.cpp behind GAMEOVER_ENABLE_UNPLAYTESTED,
// which is 0 - it has never been played. It was also written for a 9x12 board, and a 5x5
// that can be emptied completely is a different proposition.
//
// It is exposed here rather than inlined so the balancing question stays visible, and it
// is 0 because inventing a clear bonus is not this build's call to make.
// ***************************************************************************************
export interface ScoringConfig {
  readonly clearBonus: number;
}

export const DEFAULT_SCORING: ScoringConfig = Object.freeze({
  clearBonus: 0,
});
